// Pages/About.jsx
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Mission from "./Mission";
import OurStory from "./OurStory";
import ServiceAreas from "./ServiceAreas";
import FinalCTA from "../HomePage/FinalCTA";
import Founder from "./Founder";

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.2 },
  },
};

const About = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 50);
    }
  }, [hash]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white">

      {/* ===== SEO ===== */}
      <Helmet>
        <title>About Us | Ashlar Building Group</title>
        <meta
          name="description"
          content="Learn about Ashlar Building Group — a third-generation general contracting firm with 45+ years of experience in custom homes, ADUs, and remodels."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://yourdomain.com/about" />
        <meta property="og:title" content="About Us | Ashlar Building Group" />
        <meta
          property="og:description"
          content="Third-generation expertise, integrity, and craftsmanship. Discover the story behind Ashlar Building Group."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourdomain.com/about" />
      </Helmet>

      {/* ===== Hero Section ===== */}
      <div className="relative h-[30vh]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/Images/bg.jpg')" }}
        />
        <div className="absolute inset-0 bg-[#212529]/80" />

        <motion.div
          className="relative max-w-7xl mx-auto px-6 h-full flex flex-col items-center justify-center text-center"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.h1
            variants={fadeUp}
            className="text-2xl md:text-4xl font-bold text-[#f8f9fa]"
          >
            About Us
          </motion.h1>
          <motion.p
            variants={fadeUp}
            className="mt-2 text-xs md:text-sm text-[#dee2e6]"
          >
            <Link to="/" className="hover:text-[#adb5bd] transition">
              Home
            </Link>{" "}
            / <span className="text-[#f8f9fa]">About Us</span>
          </motion.p>
        </motion.div>
      </div>

      {/* ===== Founder Section ===== */}
      <Founder />

      {/* ===== Who We Are Section ===== */}
      <section className="pt-8 pb-12 md:pb-16 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 items-center lg:pr-20">

          {/* LEFT: Images — slides in from left */}
          <motion.div
            className="relative max-w-sm mx-auto lg:mx-0 lg:ml-auto grid grid-cols-2 gap-3"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="flex flex-col gap-3">
              <motion.div variants={fadeLeft} className="h-[140px] rounded-lg overflow-hidden shadow-md">
                <img src="/Images/about3.png" alt="Construction" className="w-full h-full object-cover" />
              </motion.div>
              <motion.div variants={fadeLeft} className="h-[140px] rounded-lg overflow-hidden shadow-md">
                <img src="/Images/about1.png" alt="Construction" className="w-full h-full object-cover" />
              </motion.div>
            </div>

            <div className="flex flex-col gap-3 mt-6">
              <motion.div variants={fadeLeft} className="h-[140px] rounded-lg overflow-hidden shadow-md">
                <img src="/Images/about2.png" alt="Construction" className="w-full h-full object-cover" />
              </motion.div>
              <motion.div variants={fadeLeft} className="h-[140px] rounded-lg overflow-hidden shadow-md">
                <img src="/Images/about4.png" alt="Construction" className="w-full h-full object-cover" />
              </motion.div>
            </div>

            {/* Glow */}
            <div className="absolute -top-4 -left-4 w-16 h-16 bg-[#343a40]/20 rounded-full blur-xl" />
            <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-[#343a40]/20 rounded-full blur-xl" />
          </motion.div>

          {/* RIGHT: Text Content — slides in from right */}
          <motion.div
            className="space-y-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.p
              variants={fadeRight}
              className="text-[#6c757d] uppercase tracking-wider text-xs md:text-sm font-semibold"
            >
              Who We Are
            </motion.p>

            <motion.h2
              variants={fadeRight}
              className="text-2xl md:text-3xl font-bold text-[#212529]"
            >
              Building Excellence for Generations
            </motion.h2>

            <motion.p variants={fadeRight} className="text-[#495057] text-sm leading-relaxed">
              <span className="font-semibold text-[#212529]">
                Ashlar is a third-generation general contracting firm
              </span>{" "}
              with over 45 years of hands-on experience.
            </motion.p>

            <motion.p variants={fadeRight} className="text-[#495057] text-sm leading-relaxed">
              We combine craftsmanship, integrity, and modern construction
              practices to deliver projects built to last.
            </motion.p>

            {/* Key Points */}
            <motion.div
              className="flex flex-col gap-3 pt-2"
              variants={staggerContainer}
            >
              {[
                "Third-generation expertise with 45+ years experience",
                "Deeply rooted in hands-on construction trades",
                "Fully licensed and insured professionals",
                "Quality-first approach with no shortcuts",
              ].map((text, i) => (
                <motion.div key={i} variants={fadeRight} className="flex items-start gap-2">
                  <div className="w-4 h-4 bg-[#212529] rounded-full flex items-center justify-center mt-1">
                    <svg className="w-2.5 h-2.5 text-[#f8f9fa]" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 011.414-1.414L8.414 12.172l6.879-6.879a1 1 0 011.414 0z" />
                    </svg>
                  </div>
                  <p className="text-xs md:text-sm text-[#495057]">{text}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div variants={fadeRight}>
              <Link to="/process">
                <button className="mt-4 bg-[#343a40] hover:bg-[#495057] text-[#f8f9fa] px-6 py-2.5 rounded-full text-sm font-semibold transition">
                  Learn About Our Process →
                </button>
              </Link>
            </motion.div>
          </motion.div>

        </div>
      </section>

      {/* Additional Sections */}
      <Mission />
      <OurStory />
      <ServiceAreas />
      <FinalCTA />
    </div>
  );
};

export default About;